<template>
  <Dialog
    v-if="modelValue"
    title="Create credit note"
    :custom-title="true"
    :show-close-button="true"
    :show-footer="false"
    :lg="true"
    :md="false"
    :sm="false"
    custom-width-class="!max-w-[min(64rem,96vw)]"
    :loading="submitting"
    @close-event="close"
  >
    <template #title>
      <div class="flex min-w-0 flex-col gap-0.5 pr-2">
        <h4 class="text-lg font-semibold leading-tight text-gray-900">
          Create credit note
        </h4>
        <p class="text-sm font-medium text-gray-600">
          Order #{{ orderLabel }}
          <template v-if="invoiceLabel">
            · Invoice {{ invoiceLabel }}
          </template>
        </p>
      </div>
    </template>

    <template #modal-content>
      <template v-if="!eligibility">
        <p class="rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-sm text-amber-900">
          Eligibility was not provided to the wizard. The picker should pass it through; check
          <code>SelectStripeInvoiceForCreditNoteModal</code>.
        </p>
      </template>

      <!-- Inline success state (US 5253 / Task 16179): replaces the form with a result card so the
           payment-path-specific copy (especially the finance@ instruction for non-card paid
           invoices) cannot be dismissed accidentally as a toast can. Card-paid never shows
           finance@; non-card always shows finance@; unpaid never mentions refunds. -->
      <template v-else-if="state === 'success' && submitResult">
        <div
          class="rounded-lg border px-4 py-4 shadow-sm"
          :class="successPanelClass"
          role="status"
          aria-live="polite"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-semibold"
              :class="successIconClass"
              aria-hidden="true"
            >
              ✓
            </span>
            <div class="min-w-0 flex-1">
              <h5 class="text-base font-semibold">
                {{ successHeadline }}
              </h5>
              <p class="mt-1 text-sm leading-relaxed">
                {{ paymentPathOutcomeMessage(submitResult.paymentPath) }}
              </p>
              <dl class="mt-3 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                <div class="flex items-baseline gap-2">
                  <dt class="font-medium text-current/80">Credit total</dt>
                  <dd class="font-semibold">
                    {{ formatMinor(submitResult.totalMinor) }}
                    {{ submitResult.currency }}
                  </dd>
                </div>
                <div v-if="submitResult.creditNoteId" class="flex items-baseline gap-2 truncate">
                  <dt class="font-medium text-current/80">Stripe credit note</dt>
                  <dd class="truncate font-mono text-[11px]">{{ submitResult.creditNoteId }}</dd>
                </div>
              </dl>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f]"
              @click="onSuccessDone"
            >
              Done
            </button>
          </div>
        </div>
      </template>

      <!-- Inline error state (US 5253 / Task 16179): shows Stripe / backend error message + retry
           button. Refund-failure path adds an actionable note pointing to Stripe; no success copy
           is shown for failed creates (UI never refreshes as if credited — emit only fires on
           genuine success). -->
      <template v-else-if="state === 'error' && submitError">
        <div
          class="rounded-lg border border-rose-200 bg-rose-50/90 px-4 py-4 text-rose-950 shadow-sm"
          role="alert"
          aria-live="assertive"
        >
          <div class="flex items-start gap-3">
            <span
              class="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-200 font-semibold text-rose-900"
              aria-hidden="true"
            >
              !
            </span>
            <div class="min-w-0 flex-1">
              <h5 class="text-base font-semibold text-rose-950">
                Credit note was not created
              </h5>
              <p class="mt-1 text-sm leading-relaxed text-rose-950/95 break-words">
                {{ submitError.message }}
              </p>
              <p
                v-if="submitError.isRefundFailure"
                class="mt-2 rounded-md border border-rose-300/70 bg-white/80 px-3 py-2 text-xs text-rose-900"
              >
                Stripe rejected the card refund. Verify the original payment in your Stripe
                dashboard — the charge may be too old, already refunded, or the payment source may
                no longer be valid. If a refund is still owed, issue it manually in Stripe.
              </p>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              @click="close"
            >
              Cancel
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-md border border-[#219f84] bg-white px-4 py-2 text-sm font-semibold text-[#219f84] shadow-sm transition hover:bg-[#219f84] hover:text-white"
              @click="onErrorRetry"
            >
              Edit &amp; retry
            </button>
          </div>
        </div>
      </template>

      <template v-else>
        <!-- Prior credit notes banner — visible when the invoice already has CN(s) against it.
             Full mode would try to credit the whole invoice and run into the cap; the wizard
             defaults to Partial in this case (see watch on props.modelValue / props.eligibility). -->
        <section
          v-if="eligibility.existing_credit_notes.length > 0"
          class="mb-4 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2.5 text-sm text-amber-950"
          role="note"
        >
          <p class="font-semibold">
            {{ eligibility.existing_credit_notes.length }} prior credit note{{ eligibility.existing_credit_notes.length === 1 ? '' : 's' }} against this invoice
          </p>
          <p class="mt-1 text-xs text-amber-950/95">
            Total already credited:
            <strong>
              {{ formatMinor(priorCreditedMinor) }} {{ eligibility.currency }}
            </strong>
            · Remaining creditable:
            <strong>
              {{ formatMinor(eligibility.remaining_creditable_minor) }} {{ eligibility.currency }}
            </strong>.
            Switched to <strong>Partial</strong> mode — pick lines / quantities to fit the cap, or use a free-form line for a specific amount.
          </p>
          <details class="mt-2 text-xs" open>
            <summary class="cursor-pointer select-none text-amber-900 hover:underline">
              Credit note history ({{ eligibility.existing_credit_notes.length }})
            </summary>
            <ul class="mt-1.5 space-y-2 pl-1 text-amber-950/95">
              <li
                v-for="cn in eligibility.existing_credit_notes"
                :key="cn.id"
                class="rounded-md border border-amber-200/60 bg-white/70 p-2"
              >
                <div class="flex flex-wrap items-baseline gap-x-2">
                  <span class="font-semibold text-amber-950">
                    {{ formatMinor(cn.amount_minor) }} {{ eligibility.currency }}
                  </span>
                  <span v-if="cn.number" class="text-amber-900">· {{ cn.number }}</span>
                  <span v-if="cn.status" class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                    {{ cn.status }}
                  </span>
                  <span v-if="cn.created" class="text-amber-800/80">· {{ formatCnCreated(cn.created) }}</span>
                </div>
                <div class="mt-0.5 text-[10px]">
                  <code class="rounded bg-amber-100/60 px-1 font-mono text-[10px] text-amber-900">{{ cn.id }}</code>
                  <span v-if="cn.reason" class="ml-2">
                    Reason: <span class="font-medium">{{ formatCnReason(cn.reason) }}</span>
                  </span>
                </div>
                <p v-if="cn.memo" class="mt-1 text-[11px] italic text-amber-900/95">
                  “{{ cn.memo }}”
                </p>
                <a
                  v-if="cn.pdf"
                  :href="cn.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
                >
                  Open PDF ↗
                </a>
                <span v-if="cn.pdf && cn.pdf_source === 's3'" class="ml-1 text-[10px] italic text-amber-700/70">(stored copy)</span>
              </li>
            </ul>
          </details>
        </section>

        <!-- Mode toggle -->
        <section class="mb-4">
          <div
            class="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1"
            role="tablist"
            aria-label="Credit note mode"
          >
            <button
              v-for="opt in modeOptions"
              :key="opt.value"
              type="button"
              role="tab"
              :aria-selected="mode === opt.value"
              class="rounded-md px-3 py-1.5 text-sm font-medium transition"
              :class="
                mode === opt.value
                  ? 'bg-white text-[#219f84] shadow-sm ring-1 ring-slate-200'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="switchMode(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <p class="mt-2 text-xs text-slate-500">{{ modeHint }}</p>
        </section>

        <!-- Invoice lines -->
        <section
          v-if="eligibility.lines.length > 0"
          class="mb-5 rounded-lg border border-slate-200 bg-white"
        >
          <header class="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <h5 class="text-sm font-semibold text-slate-900">Invoice lines</h5>
            <span class="text-xs text-slate-500">
              {{ eligibility.lines.length }} line{{ eligibility.lines.length === 1 ? '' : 's' }}
            </span>
          </header>
          <div class="overflow-x-auto">
            <table class="w-full min-w-[640px] border-collapse text-sm">
              <thead class="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th class="w-8 px-3 py-2 text-left" scope="col">
                    <span class="sr-only">Select</span>
                  </th>
                  <th class="px-3 py-2 text-left" scope="col">Description</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Unit</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Inv. qty</th>
                  <th class="w-28 px-3 py-2 text-right" scope="col">Credit qty</th>
                  <th class="w-32 px-3 py-2 text-right" scope="col">Credit amount</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Line total</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr
                  v-for="row in lineRows"
                  :key="row.id"
                  :class="[
                    row.selected ? '' : 'bg-slate-50/60 text-slate-400',
                    row.remainingQty <= 0 ? 'opacity-60' : '',
                  ]"
                >
                  <td class="px-3 py-2 align-middle">
                    <input
                      type="checkbox"
                      :checked="row.selected"
                      :disabled="mode === 'full' || row.remainingQty <= 0"
                      :title="row.remainingQty <= 0 ? 'This line is already fully credited' : undefined"
                      class="h-4 w-4 rounded border-slate-300 text-[#219f84] focus:ring-[#219f84] disabled:cursor-not-allowed"
                      @change="onSelectToggle(row, ($event.target as HTMLInputElement).checked)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <div class="truncate text-slate-800" :class="row.selected ? '' : 'text-slate-400'">
                      {{ row.description || '—' }}
                    </div>
                    <div class="font-mono text-[10px] text-slate-400">{{ row.id }}</div>
                    <div
                      v-if="row.alreadyCreditedQty > 0"
                      class="mt-0.5 text-[10px] font-medium text-amber-700"
                    >
                      <template v-if="row.remainingQty <= 0">
                        Already fully credited ({{ row.alreadyCreditedQty }} of {{ row.quantity }})
                      </template>
                      <template v-else>
                        {{ row.alreadyCreditedQty }} of {{ row.quantity }} already credited
                      </template>
                    </div>
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums">
                    {{ formatMinor(row.unit_amount_minor) }}
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums">
                    <div>{{ row.quantity }}</div>
                    <div
                      v-if="row.alreadyCreditedQty > 0"
                      class="text-[10px] font-normal text-amber-700"
                    >
                      ({{ row.remainingQty }} left)
                    </div>
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <input
                      type="number"
                      min="0"
                      :max="row.remainingQty"
                      step="1"
                      :disabled="!row.selected || mode === 'full' || row.amountOverridden || row.remainingQty <= 0"
                      :value="row.creditQty"
                      class="w-full rounded border border-slate-300 px-2 py-1 text-right text-sm tabular-nums outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                      @input="onQtyInput(row, ($event.target as HTMLInputElement).value)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      :disabled="!row.selected || mode === 'full' || row.remainingAmountMinor <= 0"
                      :value="row.creditAmountDisplay"
                      class="w-full rounded border border-slate-300 px-2 py-1 text-right text-sm tabular-nums outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                      placeholder="—"
                      @input="onAmountInput(row, ($event.target as HTMLInputElement).value)"
                    />
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums font-semibold text-slate-800">
                    {{ formatMinor(lineTotalMinor(row)) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Free-form lines -->
        <section class="mb-5 rounded-lg border border-slate-200 bg-white">
          <header class="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
            <div>
              <h5 class="text-sm font-semibold text-slate-900">Free-form lines</h5>
              <p class="text-xs text-slate-500">
                Adjustments that don’t correspond to an existing invoice line. Sent to Stripe as
                <code>custom_line_item</code>.
              </p>
            </div>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md border border-[#219f84] px-2.5 py-1 text-xs font-medium text-[#219f84] transition hover:bg-[#219f84] hover:text-white"
              @click="addFreeFormRow"
            >
              <PlusIcon class="h-3.5 w-3.5" />
              Add line
            </button>
          </header>
          <div v-if="freeFormRows.length > 0" class="overflow-x-auto">
            <table class="w-full min-w-[560px] border-collapse text-sm">
              <thead class="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th class="px-3 py-2 text-left" scope="col">Description</th>
                  <th class="w-24 px-3 py-2 text-right" scope="col">Qty</th>
                  <th class="w-32 px-3 py-2 text-right" scope="col">Unit amount</th>
                  <th class="px-3 py-2 text-right tabular-nums" scope="col">Line total</th>
                  <th class="w-10 px-3 py-2 text-center" scope="col">
                    <span class="sr-only">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr v-for="row in freeFormRows" :key="row.uuid">
                  <td class="px-3 py-2 align-middle">
                    <input
                      :value="row.description"
                      type="text"
                      maxlength="1000"
                      class="w-full rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
                      placeholder="e.g. Goodwill credit"
                      @input="onFreeFormDescriptionInput(row, ($event.target as HTMLInputElement).value)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <input
                      :value="row.quantity"
                      type="number"
                      min="1"
                      step="1"
                      class="w-full rounded border border-slate-300 px-2 py-1 text-right text-sm tabular-nums outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
                      @input="onFreeFormQtyInput(row, ($event.target as HTMLInputElement).value)"
                    />
                  </td>
                  <td class="px-3 py-2 align-middle">
                    <input
                      :value="row.unitAmountDisplay"
                      type="number"
                      min="0"
                      step="0.01"
                      class="w-full rounded border border-slate-300 px-2 py-1 text-right text-sm tabular-nums outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
                      placeholder="0.00"
                      @input="onFreeFormAmountInput(row, ($event.target as HTMLInputElement).value)"
                    />
                  </td>
                  <td class="px-3 py-2 text-right align-middle tabular-nums font-semibold text-slate-800">
                    {{ formatMinor(freeFormLineTotalMinor(row)) }}
                  </td>
                  <td class="px-3 py-2 text-center align-middle">
                    <button
                      type="button"
                      class="rounded-md p-1 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                      aria-label="Remove free-form line"
                      @click="removeFreeFormRow(row.uuid)"
                    >
                      <TrashIcon class="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="px-3 py-3 text-xs text-slate-500">
            No free-form lines yet. Use “Add line” to add an adjustment.
          </p>
        </section>

        <!-- Reason + memo -->
        <section class="mb-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label class="flex flex-col gap-1.5 text-sm text-slate-800">
            <span class="font-medium">Reason <span class="text-xs font-normal text-slate-500">(optional)</span></span>
            <select
              v-model="reason"
              class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
            >
              <option :value="null">— Select —</option>
              <option v-for="r in CREDIT_NOTE_REASONS" :key="r.value" :value="r.value">{{ r.label }}</option>
            </select>
          </label>
          <label class="flex flex-col gap-1.5 text-sm text-slate-800 md:col-span-1">
            <span class="font-medium">Memo <span class="text-xs font-normal text-slate-500">(optional)</span></span>
            <textarea
              v-model="memo"
              rows="2"
              maxlength="5000"
              class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm outline-none focus:border-[#219f84] focus:ring-1 focus:ring-[#219f84]/30"
              placeholder="Notes for the credit note (sent to Stripe)"
            />
          </label>
        </section>

        <!-- Summary + submit -->
        <section
          class="sticky bottom-0 -mx-1 rounded-lg border border-slate-200 bg-slate-50/90 px-3 py-3 backdrop-blur"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
              <span class="text-slate-600">
                Remaining creditable:
                <span class="font-semibold text-slate-900">
                  {{ formatMinor(eligibility.remaining_creditable_minor) }} {{ eligibility.currency }}
                </span>
              </span>
              <span class="text-slate-600">
                Credit note total:
                <span
                  class="font-semibold"
                  :class="isOverCap ? 'text-rose-600' : 'text-slate-900'"
                >
                  {{ formatMinor(totalMinor) }} {{ eligibility.currency }}
                </span>
              </span>
              <span
                v-if="paymentPathHint"
                class="inline-flex items-center rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-700"
              >
                {{ paymentPathHint }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
                :disabled="submitting"
                @click="close"
              >
                Cancel
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-md bg-[#219f84] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#1a856f] disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="!canSubmit"
                @click="submit"
              >
                <span
                  v-if="submitting"
                  class="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-r-transparent"
                />
                Create credit note
              </button>
            </div>
          </div>
          <p v-if="isOverCap" class="mt-2 text-xs font-medium text-rose-600">
            Credit note total exceeds the invoice’s remaining creditable
            ({{ formatMinor(eligibility.remaining_creditable_minor) }} {{ eligibility.currency }}).
            Reduce a line or remove a free-form row to proceed.
          </p>
          <p v-else-if="totalMinor === 0" class="mt-2 text-xs text-slate-500">
            Select at least one invoice line or add a free-form row to enable submit.
          </p>
        </section>
      </template>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import {
  CREDIT_NOTE_REASONS,
  type CreditNoteEligibility,
  type CreditNoteInvoiceLine,
  type CreditNoteReason,
  amountToMinorUnits,
  extractCreditNoteErrorMessage,
  formatMinorAsDisplay,
  isLikelyRefundFailure,
  paymentPathOutcomeMessage,
} from '@/utils/stripeCreditNote';
import { PlusIcon, TrashIcon } from '@heroicons/vue/24/outline';

type Mode = 'full' | 'partial';
type WizardState = 'idle' | 'submitting' | 'success' | 'error';

interface SubmitResult {
  paymentPath: string | null;
  totalMinor: number;
  currency: string;
  creditNoteId: string | null;
  rawCreditRow: Record<string, unknown>;
}

interface SubmitError {
  message: string;
  isRefundFailure: boolean;
}

interface LineRow {
  id: string;
  description: string | null;
  unit_amount_minor: number;
  quantity: number;
  /** Already credited via prior CNs (sum across `existing_credit_notes`). */
  alreadyCreditedQty: number;
  /** Maximum creditable qty for this row right now (`quantity - alreadyCreditedQty`). */
  remainingQty: number;
  /** Maximum creditable amount in minor units (`amount_minor - already_credited_amount_minor`). */
  remainingAmountMinor: number;
  selected: boolean;
  /** Credit quantity entered by the user; capped at `remainingQty` (not at original `quantity`). */
  creditQty: number;
  /** Explicit credit amount in minor units; takes precedence over qty when set. */
  creditAmountMinor: number | null;
  /** Display string for the amount input, kept separate from numeric state for editing UX. */
  creditAmountDisplay: string;
  /** True while an amount override is in effect (disables the qty input on the same row). */
  amountOverridden: boolean;
}

interface FreeFormRow {
  uuid: string;
  description: string;
  quantity: number;
  unitAmountMinor: number;
  unitAmountDisplay: string;
}

const props = defineProps<{
  modelValue: boolean;
  orderId: number;
  /** Local `order_item_invoices.id` — required by the backend create-credit-note endpoint. */
  orderItemInvoiceId: number;
  orderLabel: string | number;
  invoiceLabel: string | null;
  eligibility: CreditNoteEligibility | null;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'credit-note-created', payload: Record<string, unknown>): void;
}>();

const mode = ref<Mode>('full');
const reason = ref<CreditNoteReason | null>(null);
const memo = ref('');
const lineRows = ref<LineRow[]>([]);
const freeFormRows = ref<FreeFormRow[]>([]);
/** Wizard state machine — drives which surface the template renders. */
const state = ref<WizardState>('idle');
const submitResult = ref<SubmitResult | null>(null);
const submitError = ref<SubmitError | null>(null);
/** Regenerated on wizard open; reused on retry unless the form changed since last attempt. */
const idempotencyKey = ref('');
/** True when the user has edited the form after a submit attempt — drives smart key rotation. */
const formDirtyAfterLastSubmit = ref(false);

/** Submitting view is derived from state — used by the existing template + disabled props. */
const submitting = computed(() => state.value === 'submitting');

const modeOptions: Array<{ value: Mode; label: string }> = [
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
];

const modeHint = computed(() =>
  mode.value === 'full'
    ? 'Credit every line at its full invoiced quantity. Add free-form rows below for extra adjustments.'
    : 'Pick the lines you want to credit and edit quantity or amount per line. Add free-form rows for adjustments.',
);

const paymentPathHint = computed(() => {
  const path = props.eligibility?.payment_path;
  if (path === 'card_refund') return 'Card refund';
  if (path === 'out_of_band') return 'Out-of-band (finance follow-up)';
  if (path === 'unpaid') return 'Reduces amount due';
  return '';
});

/** Sum of prior CN totals against this invoice — surfaced in the "Prior credit notes" banner. */
const priorCreditedMinor = computed(() => {
  const cns = props.eligibility?.existing_credit_notes ?? [];
  let sum = 0;
  for (const cn of cns) {
    const v = Number(cn.amount_minor ?? 0);
    if (Number.isFinite(v)) sum += v;
  }
  return sum;
});

/**
 * Success panel styling shifts to amber for the non-card paid path, where a finance@ follow-up
 * is still required — green would imply the refund completed. Card-refund / unpaid stay green.
 */
const successPanelClass = computed(() => {
  if (submitResult.value?.paymentPath === 'out_of_band') {
    return 'border-amber-200 bg-amber-50/90 text-amber-950';
  }
  return 'border-emerald-200 bg-emerald-50/90 text-emerald-950';
});

const successIconClass = computed(() => {
  if (submitResult.value?.paymentPath === 'out_of_band') {
    return 'bg-amber-200 text-amber-900';
  }
  return 'bg-emerald-200 text-emerald-900';
});

const successHeadline = computed(() => {
  switch (submitResult.value?.paymentPath) {
    case 'card_refund':
      return 'Credit note created & refund issued';
    case 'out_of_band':
      return 'Credit note created — action required';
    case 'unpaid':
      return 'Credit note applied to invoice';
    default:
      return 'Credit note created';
  }
});

function makeUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function buildLineRow(line: CreditNoteInvoiceLine): LineRow {
  const alreadyCredited = Number(line.already_credited_qty ?? 0);
  const remainingQty = Number(line.remaining_qty ?? line.quantity);
  const remainingAmount = Number(line.remaining_amount_minor ?? line.amount_minor);
  return {
    id: line.id,
    description: line.description,
    unit_amount_minor: line.unit_amount_minor,
    quantity: line.quantity,
    alreadyCreditedQty: alreadyCredited,
    remainingQty,
    remainingAmountMinor: remainingAmount,
    // Auto-deselect fully-credited lines so the user doesn't have to manually uncheck them.
    selected: remainingQty > 0,
    // Pre-fill with REMAINING qty (not original quantity) so the wizard reflects what's left
    // to credit after prior CNs. User can still reduce / amount-override.
    creditQty: remainingQty,
    creditAmountMinor: null,
    creditAmountDisplay: '',
    amountOverridden: false,
  };
}

function resetRowsFromEligibility(): void {
  const lines = props.eligibility?.lines ?? [];
  lineRows.value = lines.map(buildLineRow);
  freeFormRows.value = [];
  reason.value = null;
  memo.value = '';
  state.value = 'idle';
  submitResult.value = null;
  submitError.value = null;
  formDirtyAfterLastSubmit.value = false;
}

function switchMode(next: Mode): void {
  if (mode.value === next) return;
  mode.value = next;
  if (next === 'full') {
    // Full mode in the "after-prior-CN" case still means "credit each line at its remaining
    // quantity" — credit-already lines stay unselected so submit cap matches remaining_creditable.
    for (const row of lineRows.value) {
      row.selected = row.remainingQty > 0;
      row.creditQty = row.remainingQty;
      row.creditAmountMinor = null;
      row.creditAmountDisplay = '';
      row.amountOverridden = false;
    }
  }
  markFormDirty();
}

function onSelectToggle(row: LineRow, checked: boolean): void {
  if (row.remainingQty <= 0) {
    // Fully credited — can't be re-selected (defensive; checkbox is also disabled).
    row.selected = false;
    markFormDirty();
    return;
  }
  row.selected = checked;
  if (!checked) {
    row.creditQty = 0;
    row.creditAmountMinor = null;
    row.creditAmountDisplay = '';
    row.amountOverridden = false;
  } else if (row.creditQty === 0 && !row.amountOverridden) {
    row.creditQty = row.remainingQty;
  }
  markFormDirty();
}

function onQtyInput(row: LineRow, raw: string): void {
  const next = Number(raw);
  if (!Number.isFinite(next)) {
    row.creditQty = 0;
    markFormDirty();
    return;
  }
  // Cap at remaining (not original) — user cannot credit more than what's left after prior CNs.
  row.creditQty = Math.max(0, Math.min(row.remainingQty, Math.floor(next)));
  row.creditAmountMinor = null;
  row.creditAmountDisplay = '';
  row.amountOverridden = false;
  markFormDirty();
}

function onAmountInput(row: LineRow, raw: string): void {
  row.creditAmountDisplay = raw;
  if (raw.trim() === '') {
    row.creditAmountMinor = null;
    row.amountOverridden = false;
    markFormDirty();
    return;
  }
  const currency = props.eligibility?.currency ?? 'USD';
  const minor = amountToMinorUnits(Number(raw), currency);
  // Cap at remainingAmountMinor (not gross line amount) so amount override respects prior CNs.
  const capped = Math.max(0, Math.min(minor, row.remainingAmountMinor));
  row.creditAmountMinor = capped;
  row.amountOverridden = capped > 0;
  markFormDirty();
}

function addFreeFormRow(): void {
  freeFormRows.value.push({
    uuid: makeUuid(),
    description: '',
    quantity: 1,
    unitAmountMinor: 0,
    unitAmountDisplay: '',
  });
  markFormDirty();
}

function removeFreeFormRow(uuid: string): void {
  freeFormRows.value = freeFormRows.value.filter((row) => row.uuid !== uuid);
  markFormDirty();
}

function onFreeFormDescriptionInput(row: FreeFormRow, raw: string): void {
  row.description = raw;
  markFormDirty();
}

function onFreeFormQtyInput(row: FreeFormRow, raw: string): void {
  const next = Number(raw);
  row.quantity = Number.isFinite(next) ? Math.max(1, Math.floor(next)) : 1;
  markFormDirty();
}

function onFreeFormAmountInput(row: FreeFormRow, raw: string): void {
  row.unitAmountDisplay = raw;
  if (raw.trim() === '') {
    row.unitAmountMinor = 0;
    markFormDirty();
    return;
  }
  const currency = props.eligibility?.currency ?? 'USD';
  row.unitAmountMinor = amountToMinorUnits(Number(raw), currency);
  markFormDirty();
}

function lineTotalMinor(row: LineRow): number {
  if (!row.selected) return 0;
  if (row.amountOverridden && row.creditAmountMinor !== null) {
    return row.creditAmountMinor;
  }
  // Full mode pre-fills creditQty = remainingQty (via switchMode / buildLineRow), so we just
  // use creditQty consistently across both modes — single source of truth.
  return row.creditQty * row.unit_amount_minor;
}

function freeFormLineTotalMinor(row: FreeFormRow): number {
  return Math.max(0, (row.quantity || 0) * (row.unitAmountMinor || 0));
}

const totalMinor = computed(() => {
  let total = 0;
  for (const row of lineRows.value) total += lineTotalMinor(row);
  for (const row of freeFormRows.value) total += freeFormLineTotalMinor(row);
  return total;
});

const isOverCap = computed(() => {
  const cap = props.eligibility?.remaining_creditable_minor ?? 0;
  return totalMinor.value > cap;
});

const canSubmit = computed(() => {
  if (submitting.value) return false;
  if (!props.eligibility) return false;
  if (totalMinor.value <= 0) return false;
  if (isOverCap.value) return false;
  return true;
});

function close(): void {
  if (submitting.value) return;
  // If the user dismisses (X / Cancel / backdrop) AFTER a successful create, propagate
  // the credit row so the parent still refreshes — otherwise the order would render with
  // stale data even though the CN exists on Stripe + in our ledger.
  if (state.value === 'success' && submitResult.value) {
    emit('credit-note-created', {
      credit: submitResult.value.rawCreditRow,
      payment_path: submitResult.value.paymentPath,
      credit_note_total_minor: submitResult.value.totalMinor,
    });
  }
  emit('update:modelValue', false);
}

function resolveSubmitMode(): 'full' | 'partial' | 'free_form' | 'mixed' {
  const anyMirror = lineRows.value.some((row) => row.selected && lineTotalMinor(row) > 0);
  const anyFree = freeFormRows.value.some((row) => freeFormLineTotalMinor(row) > 0);
  if (anyMirror && anyFree) return 'mixed';
  if (anyFree && !anyMirror) return 'free_form';
  if (mode.value === 'full') return 'full';
  return 'partial';
}

function buildPayload(): Record<string, unknown> {
  const invoiceLineItems: Array<Record<string, unknown>> = [];
  for (const row of lineRows.value) {
    if (!row.selected) continue;
    const lt = lineTotalMinor(row);
    if (lt <= 0) continue;
    const entry: Record<string, unknown> = { invoice_line_item: row.id };
    if (row.amountOverridden && row.creditAmountMinor !== null) {
      entry.amount = row.creditAmountMinor;
    } else if (row.creditQty > 0) {
      entry.quantity = row.creditQty;
    }
    invoiceLineItems.push(entry);
  }

  const customLineItems: Array<Record<string, unknown>> = [];
  for (const row of freeFormRows.value) {
    if (freeFormLineTotalMinor(row) <= 0) continue;
    if (!row.description.trim()) continue;
    customLineItems.push({
      description: row.description.trim(),
      quantity: Math.max(1, Math.floor(row.quantity || 1)),
      unit_amount: row.unitAmountMinor,
    });
  }

  return {
    order_item_invoice_id: props.orderItemInvoiceId,
    mode: resolveSubmitMode(),
    invoice_line_items: invoiceLineItems,
    custom_line_items: customLineItems,
    reason: reason.value,
    memo: memo.value.trim() || null,
    idempotency_key: idempotencyKey.value,
  };
}

async function submit(): Promise<void> {
  if (!canSubmit.value || !props.eligibility) return;
  if (props.orderItemInvoiceId <= 0) {
    showToast('Internal: invoice ledger id missing — close the wizard and re-open it.', 'error');
    return;
  }

  // Smart idempotency: rotate key only when the form changed since the last submit attempt.
  // A pure retry of the identical payload keeps the same key so Stripe dedupes (AC: double-
  // click → one CN). After a user edit post-error, a new key signals a genuinely new attempt.
  if (formDirtyAfterLastSubmit.value) {
    idempotencyKey.value = makeUuid();
  }

  state.value = 'submitting';
  try {
    const payload = buildPayload();
    // Backend `sendJsonResponse($message, $data)` wraps the payload under `result` (NOT `data`).
    // Shape: `{ message, result: { credit, payment_path, credit_note_total_minor }, errors: [] }`.
    const response = await http.post<{
      message: string;
      result: { credit: Record<string, unknown>; payment_path: string; credit_note_total_minor: number };
    }>(`customer-orders/${props.orderId}/credit-notes`, payload);

    const data = response.data.result;
    const paymentPath = data?.payment_path ?? props.eligibility.payment_path ?? null;
    const totalMinor = Number(data?.credit_note_total_minor ?? 0);
    const credit = (data?.credit ?? {}) as Record<string, unknown>;
    submitResult.value = {
      paymentPath,
      totalMinor: Number.isFinite(totalMinor) ? totalMinor : 0,
      currency: props.eligibility.currency,
      creditNoteId: typeof credit.stripe_credit_note_id === 'string' ? credit.stripe_credit_note_id : null,
      rawCreditRow: credit,
    };
    state.value = 'success';
    formDirtyAfterLastSubmit.value = false;
    // Note: emit('credit-note-created') fires when the user dismisses the success card via Done
    // so the parent doesn't see UI updates while the success card is still on screen.
  } catch (err) {
    const message = extractCreditNoteErrorMessage(err);
    const expectedPath = props.eligibility.payment_path;
    submitError.value = {
      message,
      isRefundFailure: isLikelyRefundFailure(message, expectedPath),
    };
    state.value = 'error';
    formDirtyAfterLastSubmit.value = false;
    // No success toast, no emit — the UI never refreshes as if credited on a failed create
    // (AC: failed create does not refresh UI as if credited).
  }
}

/** Success "Done" → delegate to `close()` so dismiss-via-X / Cancel / Done all propagate identically. */
function onSuccessDone(): void {
  close();
}

/**
 * Error "Edit & retry" → back to idle so the user can adjust before re-submitting.
 * Marks the form dirty so the next submit rotates the idempotency key — otherwise Stripe
 * would return the SAME cached error response for an unchanged-payload retry (idempotency
 * caches failures too). True double-click dedupe on the *first* submit is still preserved
 * via the `state === 'submitting'` + `canSubmit` button-disable guard.
 */
function onErrorRetry(): void {
  state.value = 'idle';
  formDirtyAfterLastSubmit.value = true;
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      idempotencyKey.value = makeUuid();
      resetRowsFromEligibility();
      // Default to Partial mode when the invoice already has credit notes against it.
      // Full mode would re-credit all lines at full qty (pre-tax sum) and exceed the
      // post-prior-credits cap — the user must pick what to credit from what's left.
      const hasPriorCns = (props.eligibility?.existing_credit_notes?.length ?? 0) > 0;
      mode.value = hasPriorCns ? 'partial' : 'full';
    }
  },
  { immediate: true },
);

watch(() => props.eligibility, () => {
  if (props.modelValue) {
    resetRowsFromEligibility();
  }
});

/**
 * Smart idempotency rotation: handlers that mutate user-controlled state call `markFormDirty()`,
 * and reactive fields with v-model (reason, memo) are watched individually. Using explicit hooks
 * avoids the false-positive a `formSignature` watch fires when `resetRowsFromEligibility()`
 * mutates the same state during open / eligibility-prop changes.
 */
function markFormDirty(): void {
  if (state.value === 'submitting') return;
  formDirtyAfterLastSubmit.value = true;
}

watch(reason, () => markFormDirty());
watch(memo, () => markFormDirty());

function formatMinor(minor: number): string {
  const currency = props.eligibility?.currency ?? 'USD';
  return formatMinorAsDisplay(minor, currency);
}

/** Stripe `created` is a unix-seconds timestamp — render as locale date+time. */
function formatCnCreated(unixSeconds: number): string {
  if (!Number.isFinite(unixSeconds) || unixSeconds <= 0) return '';
  try {
    return new Date(unixSeconds * 1000).toLocaleString();
  } catch {
    return '';
  }
}

/** Map Stripe `reason` enum to label (matches the dropdown in the wizard). */
function formatCnReason(reason: string | null | undefined): string {
  if (!reason) return '';
  const match = CREDIT_NOTE_REASONS.find((r) => r.value === reason);
  return match ? match.label : reason;
}

defineExpose({
  totalMinor,
  isOverCap,
  resolveSubmitMode,
  buildPayload,
});
</script>
